// Drizzle schema for the admin-managed template tables.
//
// Split out of `./schema.ts` so the primary tables file stays under
// the 500-line policy cap (lint:max-lines). Re-exported from
// `@citycare/shared` via index.ts so the canonical import path is
// unchanged for consumers.
//
// `buildingTypes` is a contract-scoped name plus a SET of room
// types it typically contains (via `buildingTypeRoomTypes`). Field
// staff capture actual room counts per building instance — these
// templates are reference data only.

import { index, integer, pgTable, uniqueIndex, uuid, varchar } from 'drizzle-orm/pg-core'

import { contracts, roomTypes } from './schema'
import { floorOptions } from './schema-floor-options'
import { createdAtColumn, updatedAtColumn } from './schema-shared'

// building_types — admin-managed templates that describe what kind of
// rooms a class of building typically contains (e.g. "Office Block",
// "Mess Hall", "Classroom Block"). Field staff later records actual
// instance counts per building, so this template carries a SET of
// room types via building_type_room_types but no quantities.
export const buildingTypes = pgTable(
  'building_types',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    contractId: uuid('contract_id')
      .notNull()
      .references(() => contracts.id, { onDelete: 'restrict' }),
    name: varchar('name', { length: 255 }).notNull(),
    displayOrder: integer('display_order').notNull().default(0),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    uniqueContractName: uniqueIndex(
      'building_types_contract_id_name_unique',
    ).on(t.contractId, t.name),
    contractIdx: index('building_types_contract_id_idx').on(t.contractId),
  }),
)

// building_type_room_types — many-to-many between building_types and
// room_types. No quantity column; the template just lists which room
// types may appear in this kind of building. CASCADE on building_type
// drops the junction when a template is deleted; RESTRICT on
// room_type so admins can't accidentally delete a room type that is
// referenced by a building template.
//
// `floor_option_id` is the floor each room type defaults onto when the
// template is assigned to a building. It references the contract-level
// floor picker (floor_options) — seeding resolves/creates the
// per-building `floors` row by that option's NAME, so it survives floor
// renames. Nullable: pre-floor-feature template rows (and the create
// path) leave it null, which the seeder treats as the Ground Floor.
// ON DELETE SET NULL so deleting a floor option falls the assignment
// back to Ground Floor rather than blocking the delete.
export const buildingTypeRoomTypes = pgTable(
  'building_type_room_types',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    buildingTypeId: uuid('building_type_id')
      .notNull()
      .references(() => buildingTypes.id, { onDelete: 'cascade' }),
    roomTypeId: uuid('room_type_id')
      .notNull()
      .references(() => roomTypes.id, { onDelete: 'restrict' }),
    floorOptionId: uuid('floor_option_id').references(
      () => floorOptions.id,
      { onDelete: 'set null' },
    ),
    displayOrder: integer('display_order').notNull().default(0),
    createdAt: createdAtColumn(),
  },
  (t) => ({
    uniqueBuildingTypeRoomType: uniqueIndex(
      'building_type_room_types_building_type_room_type_unique',
    ).on(t.buildingTypeId, t.roomTypeId),
    buildingTypeIdx: index(
      'building_type_room_types_building_type_id_idx',
    ).on(t.buildingTypeId),
    roomTypeIdx: index('building_type_room_types_room_type_id_idx').on(
      t.roomTypeId,
    ),
    floorOptionIdx: index(
      'building_type_room_types_floor_option_id_idx',
    ).on(t.floorOptionId),
  }),
)
